"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  placeCentered,
  placeTooltip,
  type Size,
} from "@/lib/tour/placement";
import { PRIMARY_BTN_SM, SECONDARY_BTN_SM } from "@/components/ui/button-styles";
import { routePath, type TourStep } from "@/lib/tour/tours";
import { useAnchorRect } from "./use-anchor-rect";

interface TourOverlayProps {
  step: TourStep;
  stepIndex: number;
  total: number;
  /** Current pathname; restarts the anchor seek after navigation. */
  pathname: string;
  onNext: () => void;
  onBack: () => void;
  onEnd: () => void;
  /** When set, the last step's primary button ends the tour and returns to the
   *  Guided Tours launcher (staff onboarding tours only). */
  onEndToLauncher?: () => void;
}

const DIM = "rgba(11, 13, 18, 0.55)";
const CARD_WIDTH = 360;

/**
 * Spotlight + tooltip overlay for the guided demo tour. Three fixed layers:
 * a transparent click shield (page interaction is blocked while touring),
 * the spotlight cutout (a rounded div whose enormous box-shadow dims the
 * rest of the viewport), and the tooltip card.
 */
export function TourOverlay({
  step,
  stepIndex,
  total,
  pathname,
  onNext,
  onBack,
  onEnd,
  onEndToLauncher,
}: TourOverlayProps) {
  // Hold the anchor seek until navigation lands on this step's route, so we
  // never spotlight a same-named anchor on the outgoing page. Compare on the
  // path only: a step route may carry a query (e.g. ?audience=client) that the
  // pathname never includes.
  const onRoute = pathname === routePath(step.route);
  const { phase, rect } = useAnchorRect(step.anchor, `${step.id}:${pathname}`, onRoute);
  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [cardSize, setCardSize] = useState<Size>({ width: CARD_WIDTH, height: 220 });

  // Measure the rendered card so placement math uses its real height.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (el) {
      setCardSize({ width: el.offsetWidth, height: el.offsetHeight });
    }
  }, [step.id, phase]);

  // Keep focus on Next so Enter advances the tour.
  useEffect(() => {
    nextRef.current?.focus();
  }, [step.id]);

  const viewport = { width: window.innerWidth, height: window.innerHeight };

  const pad = step.spotlightPadding ?? 8;
  // Clamp the spotlight to the viewport (slight overshoot keeps clipped
  // corners offscreen): anchors taller than the screen (e.g. the report
  // cover) would otherwise produce a box-shadow too large to paint, and
  // only the visible portion needs highlighting anyway.
  let spot = null;
  if (phase === "anchored" && rect) {
    const top = Math.max(rect.top - pad, -16);
    const left = Math.max(rect.left - pad, -16);
    const bottom = Math.min(rect.top + rect.height + pad, viewport.height + 16);
    const right = Math.min(rect.left + rect.width + pad, viewport.width + 16);
    spot = { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
  }
  const cardPos = spot
    ? placeTooltip(spot, cardSize, viewport, step.placement)
    : placeCentered(cardSize, viewport);

  const isLast = stepIndex === total - 1;

  return createPortal(
    <div>
      {/* Click shield: blocks all page interaction while the tour runs */}
      <div className="fixed inset-0 z-[200]" aria-hidden="true" />

      {/* Dim + spotlight */}
      {spot ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-[201] rounded-xl transition-[top,left,width,height] duration-200 ease-out motion-reduce:transition-none"
          style={{
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            boxShadow: `0 0 0 200vmax ${DIM}`,
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[201]"
          style={{ backgroundColor: DIM }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        className="fixed z-[202] rounded-xl bg-white p-4 shadow-[0_12px_32px_rgba(0,0,0,.35)] transition-[top,left] duration-200 ease-out motion-reduce:transition-none"
        style={{
          top: cardPos.top,
          left: cardPos.left,
          width: CARD_WIDTH,
          maxWidth: "calc(100vw - 24px)",
        }}
      >
        <span className="inline-block rounded-md bg-spark-yellow/[.15] px-2 py-0.5 text-[11px] font-bold uppercase tracking-[.08em] text-spark-yellow-text">
          Guided demo · Step {stepIndex + 1} of {total}
        </span>
        <h3 className="mt-2 text-base font-bold text-spark-black">{step.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-spark-charcoal">{step.body}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onEnd}
            className="text-xs font-medium text-spark-gray hover:text-spark-charcoal hover:underline"
          >
            End tour
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              disabled={stepIndex === 0}
              className={SECONDARY_BTN_SM}
            >
              Back
            </button>
            <button
              ref={nextRef}
              type="button"
              onClick={isLast ? (onEndToLauncher ?? onEnd) : onNext}
              className={PRIMARY_BTN_SM}
            >
              {isLast ? (onEndToLauncher ? "Back to Guided Tours" : "Finish") : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
